<template>
  <div class="page-main flex-page">
    <!--Main Content-->
    <div
      v-if="!lodash.isEmpty(selectedApp)"
      ref="mainContent"
      class="main-content"
      :class="{ 'subscription-content': subscription.status === 'active' }"
    >
      <!--Trial Plan-->
      <div v-if="subscription.status === 'trial'" class="trial-wrapper">
        <TrialDays
          :content="trialContent"
          :trial="subscription.trial"
        ></TrialDays>
        <AvailableMeteredPlans
          :content="availablePlans.content"
          :plans="availablePlans.plans"
          :hasPlanDurations="availablePlans.hasPlanDurations"
          :faqs="availablePlans.faqs"
          @subscribePlan="subscribePlan"
          class="available-metered-plans"
        ></AvailableMeteredPlans>
      </div>

      <!--Subscribed Plan-->
      <div v-if="subscription.status === 'active'" class="subscriber-wrapper">
        <SubscribedMeteredPlan
          :content="subscribedPlan.content"
          :plan="subscribedPlan.plan"
          :chargebeeSubscription="chargebeeSubscription"
          @manageSubscription="manageSubscription"
        ></SubscribedMeteredPlan>
        <Licenses class="licenses" @manageLicenses="manageLicenses"></Licenses>

        <!--License Manager-->
        <LicenseManager
          v-if="showOptions.licenseManager"
          :subscription="subscription"
          @updateLicenses="updateLicenses"
          @closeModal="closeModal"
        ></LicenseManager>
        <div
          class="bg-overlay light"
          :class="{ show: showOptions.licenseManager }"
          @click="closeModal"
        ></div>
      </div>
    </div>

    <!--Pre Loader-->
    <PreLoader v-else :show="true" class="main-content"></PreLoader>

    <!--Line Loader-->
    <LineLoader :show="showOptions.lineLoader"></LineLoader>

    <!--Notification Message-->
    <NotificationMessage
      :show="status.show"
      :status="status.status"
      :title="status.title"
      @closeModal="closeModal"
    ></NotificationMessage>
  </div>
</template>

<script>
// Libraries
import _ from "lodash";

// Components
import TrialDays from "@/components/dashboard/subscription/trial/Days";
import AvailableMeteredPlans from "@/components/dashboard/subscription/meteredPlans/AvailablePlans";
import SubscribedMeteredPlan from "@/components/dashboard/subscription/meteredPlans/SubscribedPlan";
import Licenses from "@/components/dashboard/subscription/meteredPlans/Licenses";
import LicenseManager from "@/components/dashboard/subscription/meteredPlans/LicenseManager";
import LineLoader from "@/components/loaders/LineLoader";
import PreLoader from "@/components/loaders/PreLoader";
import NotificationMessage from "@/components/modals/NotificationMessage";

// Defaults
import { APP_NAMES } from "@/mixins/Variables";

// Services
import { SubscriptionService } from "@/services";
import { TranslationsAppService } from "@/services";
const { CompanyService } = TranslationsAppService;

export default {
  name: "Subscription",
  data() {
    return {
      lodash: _,
      chargebeeSubscription: {},
      showOptions: {
        headerShadow: false,
        lineLoader: false,
        pageLoader: false,
        licenseManager: false,
      },
      trialContent: {
        title: "Free Trial",
        description: "Try the AI Ticket Translations app for free",
      },
      availablePlans: {
        content: {
          title: "Upgrade Plan",
          description:
            "Give your agents access to the best AI translations for seamless multi-lingual support",
        },
        hasPlanDurations: true,

        plans: [
          {
            title: "Business Plan",
            description: "The best AI ticket translations on Zendesk",
            isHighlighted: true,
            inclusions: [
              "Unlimted ticket translations",
              "Translate entire tickets",
              "Unlimited response translations",
              "Agent-wise language settings",
              "Dedicated support manager",
            ],
            pricing: {
              month: {
                chargebeePlanId:
                  "ai-ticket-translations-business-plan-v1-monthly",
                amount: "8",
                unit: "license",
              },
              year: {
                chargebeePlanId:
                  "ai-ticket-translations-business-plan-v1-yearly",
                amount: "6",
                unit: "license",
                isSelected: true,
              },
            },
            buttonText: "Upgrade Plan",
          },
        ],
        faqs: [
          {
            question: "Do I have to pay for all my agents?",
            answer:
              "No! You only pay for the number of licenses you buy, regardless of the number of Zendesk seats you have.",
          },
          {
            question: "What is a license?",
            answer:
              "A license is like a seat. It allows you to choose which agents get access to this app.",
          },
        ],
      },
      subscribedPlan: {
        content: {
          title: "Subscription",
          description: "Manage your plan and licenses",
        },
        plan: {
          title: "Business Plan",
          description: "The best AI ticket translations on Zendesk",
          unit: "license",
        },
      },
      status: {
        show: false,
        status: "success",
        title: "Licenses updated",
      },
    };
  },
  props: {},
  watch: {},
  components: {
    TrialDays,
    AvailableMeteredPlans,
    SubscribedMeteredPlan,
    Licenses,
    LicenseManager,
    LineLoader,
    PreLoader,
    NotificationMessage,
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    selectedApp() {
      return this.$store.getters.selectedApp;
    },
    subscription() {
      return this.selectedApp.subscription;
    },
  },
  async created() {
    await this.initApp();
    this.getSubscription();
  },
  mounted() {
    this.addChargebeeScript();
  },
  methods: {
    // Initialise the app
    async initApp() {
      if (_.isEmpty(this.selectedApp)) {
        const data = { companyId: this.company._id };
        const response = await CompanyService.GetCompany(data);

        this.$store.commit("updateSelectedApp", {
          appName: APP_NAMES.translations,
          ...response.data,
        });
      }
    },

    // Add Chargebee script to header
    addChargebeeScript() {
      let chargebeeScript = document.createElement("script");
      chargebeeScript.setAttribute(
        "src",
        "https://js.chargebee.com/v2/chargebee.js",
      );
      document.head.appendChild(chargebeeScript);
    },

    // Get subscription details from Chargebee
    async getSubscription() {
      const data = { companyId: this.company._id };
      const response = await CompanyService.GetChargebeeSubscription(data);

      if (!response.hasError) {
        this.chargebeeSubscription = response.data.subscription;
      }
    },

    // Subscribe to the plan
    async subscribePlan(planId) {
      this.showOptions.lineLoader = true;
      const response = await SubscriptionService.CreateSubscription({
        app: this.selectedApp,
        planId,
      });
      this.showOptions.lineLoader = false;

      this.chargebeeInstance = window.Chargebee.init({
        site: process.env.VUE_APP_CHARGEBEE_SITE,
        publishableKey: process.env.VUE_APP_CHARGEBEE_PUBLISHABLE_KEY,
      });

      const ele = this;
      let chargebeeInstance = window.Chargebee.getInstance();
      chargebeeInstance.openCheckout({
        hostedPage: function () {
          return new Promise(function (resolve) {
            resolve(response.data.hosted_page);
          });
        },

        success: async function () {
          ele.reloadSubscription();
        },
      });
    },

    // Manage subscription
    async manageSubscription() {
      this.showOptions.lineLoader = true;
      const data = {
        customerId: this.selectedApp?.subscription?.chargebee?.customerId,
      };
      const response = await SubscriptionService.GetPortalSession(data);
      this.showOptions.lineLoader = false;

      this.chargebeeInstance = window.Chargebee.init({
        site: process.env.VUE_APP_CHARGEBEE_SITE,
        publishableKey: process.env.VUE_APP_CHARGEBEE_PUBLISHABLE_KEY,
      });

      const chargebeeInstance = window.Chargebee.getInstance();
      chargebeeInstance.setPortalSession(function () {
        return new Promise(function (resolve) {
          resolve(response.data);
        });
      });

      let cbPortal = chargebeeInstance.createChargebeePortal();
      const ele = this;
      cbPortal.open({
        close() {
          ele.reloadSubscription();
        },
      });
    },

    // Reload the subscription view after the Chargebee portal is close
    async reloadSubscription() {
      this.showOptions.pageLoader = true;

      this.$store.commit("updateSelectedApp", {});
      await this.getUser();
      await this.initApp();

      this.showOptions.pageLoader = false;
    },

    // Manage licenses
    manageLicenses() {
      this.showOptions.licenseManager = true;
    },

    // Update the license management
    async updateLicenses(agents) {
      this.closeModal();
      this.showOptions.lineLoader = true;

      const data = { companyId: this.company._id, agents };
      const response = await CompanyService.UpdateLicenses(data);

      if (!response.hasError) {
        this.$store.commit("updateSelectedApp", {
          appName: APP_NAMES.translations,
          ...response.data,
        });
      }

      this.showStatusMessage(this.status, 2500);
      this.showOptions.lineLoader = false;
    },

    closeModal() {
      this.showOptions.licenseManager = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/shared/dashboard.scss";

.main-content {
  padding: 0;

  .licenses,
  .subscriber-wrapper,
  &.subscription-content {
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}

.scroll-main {
  flex: 1;
  padding: 2rem 1.5rem;
  overflow-y: scroll;
}

.available-metered-plans {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid $borderColor;
}

.licenses {
  margin-top: 3rem;
}

.page-loader-wrapper {
  margin-top: 2rem;
}

::v-deep .line-loader {
  position: fixed;
  z-index: 101;
  bottom: 0;
  left: 0;
}

.show-shadow {
  box-shadow: 0 0.5rem 0.7rem -0.5rem $boxShadowColor;
}
</style>
